import React, { useState, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { useWistorAuth } from '../../api/auth'
import { useProjectsWithCounts, useCreateProject, useRenameProject, useDeleteProject } from '../shared/use-v2-queries'
import { useApiClient } from '../shared/api-client'
import { useQuery } from '@tanstack/react-query'
import { useApiBase } from '../shared/api-base-provider'
import orderBy from 'lodash/orderBy'

// ─── Icons ────────────────────────────────────────────
const ic = {
  search: 'M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z',
  plus: 'M12 5v14M5 12h14',
  folder: 'M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z',
  edit: 'M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z',
  trash: 'M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2',
  x: 'M18 6L6 18M6 6l12 12',
}

function Icon({ d, size = 16 }: { d: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />
    </svg>
  )
}

// ─── Dialog Component (using v2 theme classes) ────────
function V2Dialog({
                    open,
                    onClose,
                    title,
                    children,
                    footer,
                  }: {
  open: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  footer: React.ReactNode
}) {
  if (!open) return null
  return (
    <div className="v2-dialog-overlay" onClick={onClose}>
      <div className="v2-dialog" onClick={e => e.stopPropagation()}>
        <div className="v2-dialog-header">
          <div className="v2-dialog-title">{title}</div>
        </div>
        <div className="v2-dialog-body">{children}</div>
        <div className="v2-dialog-footer">{footer}</div>
      </div>
    </div>
  )
}

// ─── Main Export ───────────────────────────────────────
export function HomeV2() {
  const auth = useWistorAuth()
  const history = useHistory()

  React.useEffect(() => {
    if (auth.status !== 'authorized') return history.push('/login', '')
    if (auth.user.role !== 'superadmin' && auth.user.project) {
      return history.replace(`/projects/${auth.user.project.path}`)
    }
  }, [auth, history])

  if (auth.status !== 'authorized') return null
  if (auth.user.role !== 'superadmin' && auth.user.project) return null

  return <SuperadminDashboard />
}

// ─── Dashboard ────────────────────────────────────────
function SuperadminDashboard() {
  const auth = useWistorAuth()
  const history = useHistory()
  const isAdmin = auth.status === 'authorized' && ['admin', 'superadmin'].includes(auth.user.role)
  const projectsQuery = useProjectsWithCounts({ isAdmin })
  const createProject = useCreateProject()
  const renameProject = useRenameProject()
  const deleteProject = useDeleteProject()

  const [search, setSearch] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const [createValues, setCreateValues] = useState({ title: '', path: '' })
  const [renameOpen, setRenameOpen] = useState<{ id: string; title: string; path: string } | null>(null)
  const [deleteOpen, setDeleteOpen] = useState<{ id: string; title: string; appCount: number } | null>(null)

  const projects = useMemo(() => {
    if (!projectsQuery.data) return []
    let items = orderBy(projectsQuery.data, ['title'], ['asc'])
    if (search.trim()) {
      const q = search.toLowerCase()
      items = items.filter((p: any) =>
        p.title?.toLowerCase().includes(q) || p.path?.toLowerCase().includes(q)
      )
    }
    return items
  }, [projectsQuery.data, search])

  const handleCreate = () => {
    if (!createValues.title.trim()) return
    createProject.mutate(
      { title: createValues.title.trim(), path: createValues.path.trim() || undefined },
      {
        onSuccess: () => { setCreateOpen(false); setCreateValues({ title: '', path: '' }) },
        onError: () => alert('Failed to create project.'),
      }
    )
  }

  const handleRename = () => {
    if (!renameOpen?.title.trim()) return
    renameProject.mutate(
      { id: renameOpen.id, title: renameOpen.title.trim(), path: renameOpen.path.trim() || undefined },
      {
        onSuccess: () => setRenameOpen(null),
        onError: () => alert('Failed to rename project.'),
      }
    )
  }

  const handleDelete = () => {
    if (!deleteOpen) return
    deleteProject.mutate(deleteOpen.id, {
      onSuccess: () => setDeleteOpen(null),
      onError: (err: any) => {
        const msg = err?.response?.data?.message || err?.response?.data || err?.message || 'Failed to delete project.'
        alert(typeof msg === 'string' ? msg : 'Failed to delete project.')
      },
    })
  }

  if (projectsQuery.isLoading) {
    return <div className="v2-spinner" />
  }

  // Compute stats from the enriched data
  const totalApps = projects.reduce((sum: number, p: any) => sum + (p.viewerCount ?? 0), 0)
  // _totalUsers includes users without a project (e.g. superadmins),
  // so use it directly rather than summing per-project userCount.
  const totalUsers = projects.length > 0 ? (projects[0]._totalUsers ?? 0) : 0

  return (
    <div className="v2-page">
      {/* Header */}
      <div className="v2-page-header">
        <div>
          <h1 className="v2-page-title">Projects</h1>
          <p className="v2-page-subtitle">Manage your platform deployments</p>
        </div>
        <button className="v2-btn v2-btn--primary" onClick={() => setCreateOpen(true)}>
          <Icon d={ic.plus} size={14} />
          New Project
        </button>
      </div>

      {/* ─── Insights / Stat Bar ─── */}
      <div className="v2-stats">
        <div className="v2-stat">
          <div className="v2-stat-value">{projects.length}</div>
          <div className="v2-stat-label">Projects</div>
        </div>
        <div className="v2-stat">
          <div className="v2-stat-value">{totalApps}</div>
          <div className="v2-stat-label">Total Apps</div>
        </div>
        <div className="v2-stat">
          <div className="v2-stat-value">{totalUsers}</div>
          <div className="v2-stat-label">Users</div>
        </div>
      </div>

      {/* Search */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <div className="v2-search-wrap" style={{ flex: 1, minWidth: 200 }}>
          <span className="v2-search-icon"><Icon d={ic.search} size={15} /></span>
          <input
            className="v2-input"
            placeholder="Search projects..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ paddingLeft: 38 }}
          />
        </div>
        <span style={{ fontSize: 12, color: 'var(--v2-text-muted)' }}>
          {projects.length} results
        </span>
      </div>

      {/* ─── Quick Go-To App Search ─── */}
      <AppQuickSearch />

      {/* Project grid */}
      <div className="v2-card-grid">
        {projects.map((project: any) => (
          <div
            key={project.id || project._id}
            className="v2-card"
            onClick={() => history.push(`/projects/${project.path}`)}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <div className="v2-card-icon">
                <Icon d={ic.folder} size={15} />
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="v2-card-title">{project.title}</div>
                <div className="v2-path">/{project.path}</div>
              </div>
              {/* Rename button */}
              <button
                type="button"
                className="v2-action v2-action--icon-only"
                onClick={(e) => {
                  e.stopPropagation()
                  setRenameOpen({ id: project.id || project._id, title: project.title, path: project.path })
                }}
                title="Rename"
              >
                <Icon d={ic.edit} size={13} />
              </button>
              {/* Delete button */}
              <button
                type="button"
                className="v2-action v2-action--icon-only v2-action--danger"
                onClick={(e) => {
                  e.stopPropagation()
                  setDeleteOpen({
                    id: project.id || project._id,
                    title: project.title,
                    appCount: project.viewerCount ?? 0,
                  })
                }}
                title="Delete"
              >
                <Icon d={ic.trash} size={13} />
              </button>
            </div>
            <div className="v2-card-footer">
              <div>
                <span className="v2-card-metric">{project.viewerCount ?? 0}</span>
                <span className="v2-card-metric-label">apps</span>
              </div>
              <div>
                <span className="v2-card-metric">{project.userCount ?? 0}</span>
                <span className="v2-card-metric-label">users</span>
              </div>
              <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--v2-text-muted)' }}>
                {project.updatedAt
                  ? new Date(project.updatedAt).toLocaleDateString()
                  : ''}
              </span>
            </div>
          </div>
        ))}
      </div>

      {projects.length === 0 && !projectsQuery.isLoading && (
        <div className="v2-empty">
          <div className="v2-empty-text">
            {search ? 'No projects match your search.' : 'No projects yet.'}
          </div>
          {!search && (
            <div className="v2-empty-hint">Create your first project to get started.</div>
          )}
        </div>
      )}

      {/* ─── Create Dialog ─── */}
      <V2Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Create Project"
        footer={
          <>
            <button className="v2-btn v2-btn--ghost" onClick={() => setCreateOpen(false)}>Cancel</button>
            <button className="v2-btn v2-btn--primary" onClick={handleCreate} disabled={createProject.isPending}>
              {createProject.isPending ? 'Creating...' : 'Create'}
            </button>
          </>
        }
      >
        <div className="v2-field">
          <label className="v2-label">Title</label>
          <input
            className="v2-input"
            value={createValues.title}
            onChange={e => setCreateValues(v => ({ ...v, title: e.target.value }))}
            placeholder="My Project"
            autoFocus
          />
        </div>
        <div className="v2-field">
          <label className="v2-label">Path (optional)</label>
          <input
            className="v2-input"
            value={createValues.path}
            onChange={e => setCreateValues(v => ({ ...v, path: e.target.value }))}
            placeholder="Auto-generated from title if empty"
          />
        </div>
      </V2Dialog>

      {/* ─── Rename Dialog ─── */}
      <V2Dialog
        open={!!renameOpen}
        onClose={() => setRenameOpen(null)}
        title="Rename Project"
        footer={
          <>
            <button className="v2-btn v2-btn--ghost" onClick={() => setRenameOpen(null)}>Cancel</button>
            <button className="v2-btn v2-btn--primary" onClick={handleRename} disabled={renameProject.isPending}>
              {renameProject.isPending ? 'Saving...' : 'Save'}
            </button>
          </>
        }
      >
        {renameOpen && (
          <>
            <div className="v2-field">
              <label className="v2-label">Title</label>
              <input
                className="v2-input"
                value={renameOpen.title}
                onChange={e => setRenameOpen(d => d ? { ...d, title: e.target.value } : d)}
                autoFocus
              />
            </div>
            <div className="v2-field">
              <label className="v2-label">Path</label>
              <input
                className="v2-input"
                value={renameOpen.path}
                onChange={e => setRenameOpen(d => d ? { ...d, path: e.target.value } : d)}
              />
            </div>
          </>
        )}
      </V2Dialog>

      {/* ─── Delete Dialog ─── */}
      <V2Dialog
        open={!!deleteOpen}
        onClose={() => setDeleteOpen(null)}
        title="Delete Project"
        footer={
          <>
            <button className="v2-btn v2-btn--ghost" onClick={() => setDeleteOpen(null)}>Cancel</button>
            <button className="v2-btn v2-btn--danger" onClick={handleDelete} disabled={deleteProject.isPending}>
              {deleteProject.isPending ? 'Deleting...' : 'Delete'}
            </button>
          </>
        }
      >
        {deleteOpen && (
          <>
            <p style={{ fontSize: 14, color: 'var(--v2-text-secondary)', lineHeight: 1.6 }}>
              Are you sure you want to delete <strong>"{deleteOpen.title}"</strong>?
            </p>
            {deleteOpen.appCount > 0 && (
              <p style={{ fontSize: 13, color: 'var(--v2-warning)', marginTop: 8, lineHeight: 1.6 }}>
                This project has <strong>{deleteOpen.appCount} active app{deleteOpen.appCount !== 1 ? 's' : ''}</strong>.
                You must delete or move all apps before the project can be deleted.
              </p>
            )}
            <p style={{ marginTop: 8, fontSize: 12, color: 'var(--v2-text-muted)' }}>
              This is a soft delete — the project can be recovered later.
            </p>
          </>
        )}
      </V2Dialog>
    </div>
  )
}

// ─── Quick Go-To App Search ───────────────────────────
type AppSearchResult = {
  title: string
  path: string
  project: { title: string; path: string }
}

function AppQuickSearch() {
  const api = useApiClient()
  const { baseUrl } = useApiBase()
  const history = useHistory()
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)

  const appsQuery = useQuery({
    queryKey: ['v2', baseUrl, 'all-apps-search'],
    staleTime: 60_000,
    queryFn: async () => {
      const { data } = await api.get('/api/list-viewers')
      return (data as any[]).filter(
        (a: any) => a?.path && a?.project?.path
      ) as AppSearchResult[]
    },
  })

  const results = useMemo(() => {
    if (!appsQuery.data || !search.trim()) return []
    const q = search.toLowerCase()
    return appsQuery.data
      .filter(a =>
        a.title?.toLowerCase().includes(q) ||
        a.path?.toLowerCase().includes(q) ||
        a.project?.title?.toLowerCase().includes(q)
      )
      .slice(0, 12)
  }, [appsQuery.data, search])

  // Group results by project
  const grouped = useMemo(() => {
    const map = new Map<string, { label: string; items: AppSearchResult[] }>()
    for (const app of results) {
      const key = app.project.path
      if (!map.has(key)) map.set(key, { label: app.project.title, items: [] })
      map.get(key)!.items.push(app)
    }
    return Array.from(map.values())
  }, [results])

  const goToApp = (app: AppSearchResult) => {
    history.push(`/projects/${app.project.path}`)
    setSearch('')
    setOpen(false)
  }

  return (
    <div className="v2-app-search" style={{ marginBottom: 24, position: 'relative' }}>
      <div className="v2-search-wrap" style={{ width: '100%' }}>
        <span className="v2-search-icon">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" />
          </svg>
        </span>
        <input
          className="v2-input"
          placeholder="Quick find: search all apps across projects..."
          value={search}
          onChange={e => { setSearch(e.target.value); setOpen(true) }}
          onFocus={() => { setOpen(true); appsQuery.refetch() }}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          style={{ paddingLeft: 38 }}
        />
      </div>

      {open && search.trim() && (
        <div className="v2-app-search-dropdown">
          {appsQuery.isLoading ? (
            <div style={{ padding: '12px 16px', fontSize: 13, color: 'var(--v2-text-muted)' }}>
              Loading apps...
            </div>
          ) : results.length === 0 ? (
            <div style={{ padding: '12px 16px', fontSize: 13, color: 'var(--v2-text-muted)' }}>
              No apps found for "{search}"
            </div>
          ) : (
            grouped.map(group => (
              <div key={group.label}>
                <div style={{
                  padding: '8px 16px 4px',
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: '0.07em',
                  textTransform: 'uppercase',
                  color: 'var(--v2-text-muted)',
                }}>
                  {group.label}
                </div>
                {group.items.map(app => (
                  <button
                    key={`${app.project.path}/${app.path}`}
                    className="v2-more-item"
                    style={{ width: '100%' }}
                    onMouseDown={(e) => { e.preventDefault(); goToApp(app) }}
                  >
                    <span style={{
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {app.title}
                    </span>
                    <span style={{ fontSize: 11, opacity: 0.5, marginLeft: 8 }}>
                      /{app.path}
                    </span>
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}